import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useToast } from '../../hooks/use-toast';
import { crmTerritorialAPI, orgAPI } from '../../services/api';
import ProspectoAcciones from '../../components/admin/ProspectoAcciones';
import ClienteCoincidencias from '../../components/admin/ClienteCoincidencias';
import OportunidadEtapa from '../../components/admin/OportunidadEtapa';
import MetaTerritorialForm from '../../components/admin/MetaTerritorialForm';
import ProspectoNuevo from '../../components/admin/ProspectoNuevo';
import ReasignacionMasiva from '../../components/admin/ReasignacionMasiva';
import PropiedadForm from '../../components/admin/PropiedadForm';
import AgenteInventario from '../../components/admin/AgenteInventario';
import AgenteAtencion from '../../components/admin/AgenteAtencion';
import AgenteAfiliacion from '../../components/admin/AgenteAfiliacion';
import PanelSupervisionAgentes from '../../components/admin/PanelSupervisionAgentes';
import PanelAudienciasMarketing from '../../components/admin/PanelAudienciasMarketing';
import { Loader2, Users, Target, Home, UserCircle, ClipboardList, TrendingUp, BarChart3, Download, Flag, ScrollText, Activity } from 'lucide-react';

const VISTAS = [
  { id: 'asesores', label: 'Asesores', icon: UserCircle },
  { id: 'clientes', label: 'Clientes', icon: Users },
  { id: 'prospectos', label: 'Prospectos', icon: Target },
  { id: 'oportunidades', label: 'Oportunidades', icon: TrendingUp },
  { id: 'desempeno', label: 'Desempeño', icon: BarChart3 },
  { id: 'metas', label: 'Metas', icon: Flag },
  { id: 'auditoria', label: 'Auditoría', icon: ScrollText },
  { id: 'propiedades', label: 'Propiedades', icon: Home },
  { id: 'actividades', label: 'Bitácora', icon: ClipboardList },
  { id: 'supervision', label: 'Supervisión', icon: Activity },
  { id: 'audiencias', label: 'Audiencias', icon: Users },
];

const dinero = (v) => (v == null ? '—' : `$${Number(v).toLocaleString('es-MX')}`);
const fecha = (v) => (v ? new Date(v).toLocaleDateString('es-MX') : '—');

// P3H — semáforo determinista de cumplimiento.
const SEMAFORO = {
  verde: 'bg-emerald-100 text-emerald-800',
  ambar: 'bg-amber-100 text-amber-800',
  rojo: 'bg-red-100 text-red-800',
  sin_meta: 'bg-gray-100 text-gray-700',
};

const Avance = ({ d, dinero: esDinero, sufijo = '' }) => {
  if (!d || d.semaforo === 'sin_meta') return <span className="text-gray-400">—</span>;
  const real = esDinero ? dinero(d.real) : `${d.real}${sufijo}`;
  const meta = esDinero ? dinero(d.objetivo) : `${d.objetivo}${sufijo}`;
  return (
    <span className={d.semaforo === 'rojo' ? 'text-red-600' : ''}>
      {real} / {meta} · {d.avance}%{d.en_ritmo === false && ' ⚠'}
    </span>
  );
};

const Tabla = ({ columnas, filas, testid }) => (
  <div className="overflow-x-auto" data-testid={testid}>
    <table className="w-full text-sm">
      <thead className="text-left text-gray-500 border-b">
        <tr>{columnas.map((c) => <th key={c} className="py-2 pr-4 font-medium">{c}</th>)}</tr>
      </thead>
      <tbody>
        {filas.length === 0 && (
          <tr><td colSpan={columnas.length} className="py-6 text-gray-500">
            Sin registros en tu alcance territorial.
          </td></tr>
        )}
        {filas.map((f, i) => (
          <tr key={i} className="border-b last:border-0 hover:bg-gray-50 transition-colors">
            {f.map((celda, j) => <td key={j} className="py-2 pr-4">{celda}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// P3A — Vistas centrales del CRM por territorio. Cada sección ve sólo la suya; Nacional consolida.
const AdminCRMTerritorial = () => {
  const [secciones, setSecciones] = useState([]);
  const [seccion, setSeccion] = useState('');
  const [vista, setVista] = useState('asesores');
  const [resumen, setResumen] = useState(null);
  const [datos, setDatos] = useState(null);
  const [vistaDatos, setVistaDatos] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [seleccionado, setSeleccionado] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [oportunidad, setOportunidad] = useState(null);
  const [dias, setDias] = useState('');
  const [soloInactivas, setSoloInactivas] = useState(false);
  const [nuevaMeta, setNuevaMeta] = useState(false);
  const [nuevoProspecto, setNuevoProspecto] = useState(false);
  const [seleccion, setSeleccion] = useState([]);
  const [reasignando, setReasignando] = useState(false);
  const [propForm, setPropForm] = useState(null);
  const [agente, setAgente] = useState(false);
  const [agenteAtencion, setAgenteAtencion] = useState(false);
  const [agenteAfiliacion, setAgenteAfiliacion] = useState(false);
  const [filtros, setFiltros] = useState({ actor: '', accion: '', resultado: '', desde: '',
    hasta: '' });
  const [listaAsesores, setListaAsesores] = useState([]);
  const { toast } = useToast();

  const cargar = async (sec, v, n) => {
    setCargando(true);
    try {
      const [r, d] = await Promise.all([
        crmTerritorialAPI.resumen(sec || undefined),
        (v === 'supervision' || v === 'audiencias')
          ? Promise.resolve(null)
          : v === 'oportunidades'
          ? crmTerritorialAPI.oportunidades(sec || undefined, n || undefined)
          : v === 'desempeno'
            ? crmTerritorialAPI.desempeno(sec || undefined, n || undefined)
            : v === 'metas'
              ? crmTerritorialAPI.metas(sec || undefined)
              : v === 'auditoria'
                ? crmTerritorialAPI.bitacora(sec || undefined, filtros)
                : crmTerritorialAPI[v](sec || undefined),
      ]);
      setResumen(r);
      setDatos(d);
      setVistaDatos(v);
    } catch (e) {
      toast({ title: 'Consulta rechazada', description: e.message, variant: 'destructive' });
    } finally { setCargando(false); }
  };

  useEffect(() => {
    orgAPI.getSections().then((s) => setSecciones(s || [])).catch(() => setSecciones([]));
  }, []);
  useEffect(() => {
    crmTerritorialAPI.asesores().then((d) => setListaAsesores(d.items || []))
      .catch(() => setListaAsesores([]));
  }, []);
  useEffect(() => { setSeleccion([]); cargar(seccion, vista, dias); }, [seccion, vista, dias]);

  const toggleSel = (id) => setSeleccion((s) => (s.includes(id)
    ? s.filter((x) => x !== id) : [...s, id]));

  const payload = vistaDatos === vista ? datos : null;
  const items = ((payload?.items) || []).filter(
    (x) => !(vista === 'oportunidades' && soloInactivas) || x.inactiva);
  const TABLAS = {
    asesores: () => ({
      columnas: ['Asesor', 'Sección', 'Clientes', 'Prospectos', 'Propiedades', 'Pendientes'],
      filas: items.map((x) => [x.nombre, x.seccion, x.clientes, x.prospectos, x.propiedades,
        x.actividades_pendientes]),
    }),
    clientes: () => ({
      columnas: ['Cliente', 'Tipo', 'Necesidad', 'Asesor', 'Sección', ''],
      filas: items.map((x) => [x.nombre, x.tipo,
        `${dinero(x.necesidad?.presupuesto_min)} – ${dinero(x.necesidad?.presupuesto_max)}`,
        x.asesor?.nombre || '—', x.seccion,
        <Button key="c" size="sm" variant="outline" onClick={() => setCliente(x)}
                data-testid={`cliente-coincidencias-${x.contacto_id}`}>
          Necesidad
        </Button>]),
    }),
    prospectos: () => ({
      columnas: ['', 'Prospecto', 'Etapa', 'Contacto', 'Asesor', 'Seguimiento',
        'Próxima acción', ''],
      filas: items.map((x) => [
        <input key="sel" type="checkbox" checked={seleccion.includes(x.oportunidad_id)}
               onChange={() => toggleSel(x.oportunidad_id)}
               data-testid={`prospecto-seleccion-${x.oportunidad_id}`} />,
        x.nombre,
        <Badge key="e" className="bg-blue-100 text-blue-800">{x.etapa}</Badge>,
        x.contacto?.nombre || '—', x.asesor?.nombre || '—',
        `${x.seguimiento?.completadas ?? 0}/${x.seguimiento?.actividades_registradas ?? 0} hechas`,
        fecha(x.seguimiento?.proxima),
        <Button key="a" size="sm" variant="outline" onClick={() => setSeleccionado(x)}
                data-testid={`prospecto-acciones-${x.oportunidad_id}`}>
          Gestionar
        </Button>]),
    }),
    propiedades: () => ({
      columnas: ['Propiedad', 'Tipo', 'Operación', 'Estado', 'Precio', 'Asesor', ''],
      filas: items.map((x) => [x.nombre, x.tipo, x.operacion,
        <Badge key="s" className="bg-emerald-100 text-emerald-800">{x.estado_inmueble}</Badge>,
        dinero(x.precio), x.asesor?.nombre || '—',
        <Button key="e" size="sm" variant="outline" onClick={() => setPropForm(x)}
                data-testid={`propiedad-editar-${x.propiedad_id}`}>
          Editar
        </Button>]),
    }),
    actividades: () => ({
      columnas: ['Asunto', 'Tipo', 'Estado', 'Asesor', 'Sección', 'Fecha'],
      filas: items.map((x) => [x.asunto, x.tipo, x.estado, x.asesor?.nombre || '—', x.seccion,
        fecha(x.fecha)]),
    }),
    oportunidades: () => ({
      columnas: ['Oportunidad', 'Etapa', 'Monto', 'Próxima acción', 'Vence', 'Inactividad',
        'Asesor', ''],
      filas: items.map((x) => [x.nombre,
        <Badge key="e" className={x.cerrada ? 'bg-gray-100 text-gray-700'
                                            : 'bg-indigo-100 text-indigo-800'}>{x.etapa}</Badge>,
        dinero(x.monto), x.proxima_accion?.descripcion || '—',
        <span key="v" className={x.proxima_accion?.atrasada ? 'text-red-600 font-medium' : ''}>
          {fecha(x.proxima_accion?.vence)}
        </span>,
        x.inactiva
          ? <Badge key="i" className="bg-amber-100 text-amber-800"
                   data-testid={`oportunidad-inactiva-${x.oportunidad_id}`}>
              {x.dias_sin_actividad} d
            </Badge>
          : <span key="i" className="text-gray-400">{x.dias_sin_actividad ?? '—'} d</span>,
        x.asesor?.nombre || '—',
        <Button key="p" size="sm" variant="outline" onClick={() => setOportunidad(x)}
                data-testid={`oportunidad-etapa-btn-${x.oportunidad_id}`}>
          Etapa
        </Button>]),
    }),
    desempeno: () => ({
      columnas: ['Asesor', 'Sección', 'Oport.', 'Ganadas', 'Perdidas', 'Conversión',
        'Monto ganado', 'Días cierre', 'Inactivas', 'Actividades'],
      filas: items.map((x) => [x.asesor, x.seccion, x.oportunidades, x.ganadas, x.perdidas,
        <Badge key="c" className="bg-emerald-100 text-emerald-800">
          {x.tasa_de_conversion}%
        </Badge>,
        dinero(x.monto_ganado), x.dias_promedio_cierre ?? '—',
        x.inactivas > 0
          ? <Badge key="i" className="bg-amber-100 text-amber-800">{x.inactivas}</Badge>
          : '0',
        `${x.actividades_completadas} hechas · ${x.actividades_atrasadas} atrasadas`]),
    }),
    metas: () => ({
      columnas: ['Ámbito', 'Sección', 'Periodo', 'Semáforo', 'Oportunidades', 'Cierres', 'Monto',
        'Conversión', ''],
      filas: items.map((x) => [
        x.ambito === 'asesor' ? (x.asesor || 'Asesor') : 'Toda la sección', x.seccion,
        `${fecha(x.periodo_inicio)} – ${fecha(x.periodo_fin)} (${x.periodo_transcurrido_pct}%)`,
        <Badge key="s" className={SEMAFORO[x.semaforo]}
               data-testid={`meta-semaforo-${x.meta_id}`}>{x.semaforo}</Badge>,
        <Avance key="o" d={x.indicadores.oportunidades} />,
        <Avance key="c" d={x.indicadores.cierres} />,
        <Avance key="m" d={x.indicadores.monto} dinero />,
        <Avance key="v" d={x.indicadores.conversion} sufijo="%" />,
        <Button key="d" size="sm" variant="outline"
                data-testid={`meta-desactivar-${x.meta_id}`}
                onClick={() => crmTerritorialAPI.desactivarMeta(x.meta_id)
                  .then(() => { toast({ title: 'Meta desactivada' }); cargar(seccion, vista, dias); })
                  .catch((e) => toast({ title: 'No se pudo desactivar', description: e.message,
                    variant: 'destructive' }))}>
          Desactivar
        </Button>]),
    }),
    auditoria: () => ({
      columnas: ['Fecha', 'Actor', 'Sección', 'Acción', 'Entidad', 'Resultado', 'Motivo',
        'Cambio'],
      filas: items.map((x, i) => [
        x.fecha ? new Date(x.fecha).toLocaleString('es-MX') : '—', x.actor, x.seccion, x.accion,
        x.entidad,
        <Badge key="r" className={x.resultado === 'denied' ? 'bg-red-100 text-red-800'
                                                          : 'bg-emerald-100 text-emerald-800'}
               data-testid={`bitacora-resultado-${i}`}>{x.resultado}</Badge>,
        <span key="m" className="text-xs">{x.motivo || '—'}</span>,
        <span key="c" className="text-xs text-gray-500">
          {x.antes || x.despues
            ? Object.entries(x.despues || {}).slice(0, 3)
              .map(([k, v]) => `${k}: ${typeof v === 'object' ? JSON.stringify(v) : v}`).join(' · ')
            : '—'}
        </span>]),
    }),
  };
  const tabla = (TABLAS[vista] || (() => ({ columnas: [], filas: [] })))();

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="crm-territorial-page">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">CRM territorial</h1>
            <p className="text-sm text-gray-500">
              Asesores, clientes, prospectos y propiedades de tu territorio.
              {resumen && ` Alcance: ${resumen.alcance}.`}
            </p>
          </div>
          <select value={seccion} onChange={(e) => setSeccion(e.target.value)}
                  data-testid="crm-territorial-selector-seccion"
                  className="border rounded-lg px-3 py-2 text-sm bg-white">
            <option value="">Todo mi alcance</option>
            {secciones.map((s) => (
              <option key={s.id} value={s.id}>{s.name} ({s.abbreviation})</option>
            ))}
          </select>
        </div>

        {resumen && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3"
               data-testid="crm-territorial-resumen">
            {Object.entries(resumen.totales).map(([k, v]) => (
              <Card key={k}>
                <CardContent className="p-4">
                  <p className="text-xs text-gray-500 capitalize">{k.replace(/_/g, ' ')}</p>
                  <p className="text-2xl font-bold text-gray-900">{v}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {VISTAS.map(({ id, label, icon: Icon }) => (
            <Button key={id} variant={vista === id ? 'default' : 'outline'} size="sm"
                    onClick={() => setVista(id)} data-testid={`crm-territorial-tab-${id}`}>
              <Icon className="h-4 w-4 mr-2" />{label}
            </Button>
          ))}
        </div>

        {vista === 'oportunidades' && payload && (
          <div className="flex flex-wrap items-center gap-3 text-sm"
               data-testid="oportunidades-alerta-inactividad">
            <span className="text-gray-500">Sin actividad en</span>
            <input type="number" min="1" max="365" value={dias}
                   placeholder={String(payload.inactividad_dias_defecto ?? 15)}
                   onChange={(e) => setDias(e.target.value)}
                   className="w-20 border rounded-lg px-2 py-1"
                   data-testid="oportunidades-inactividad-dias" />
            <span className="text-gray-500">día(s):</span>
            <Badge className="bg-amber-100 text-amber-800"
                   data-testid="oportunidades-inactivas-contador">
              {payload.metricas?.sin_actividad_n_dias ?? 0} oportunidad(es)
            </Badge>
            <Button size="sm" variant={soloInactivas ? 'default' : 'outline'}
                    onClick={() => setSoloInactivas((v) => !v)}
                    data-testid="oportunidades-filtro-inactivas">
              {soloInactivas ? 'Ver todas' : 'Ver sólo inactivas'}
            </Button>
            <span className="text-gray-400 text-xs">
              Alerta interna: no genera correos ni mensajes externos.
            </span>
          </div>
        )}

        {vista === 'desempeno' && payload && (
          <div className="flex flex-wrap items-center gap-3 text-sm"
               data-testid="desempeno-barra">
            <Badge className="bg-indigo-100 text-indigo-800" data-testid="desempeno-conversion">
              Conversión global: {payload.totales?.tasa_de_conversion ?? 0}%
            </Badge>
            <span className="text-gray-500">
              Ganadas {payload.totales?.ganadas ?? 0} · Perdidas {payload.totales?.perdidas ?? 0} ·
              Abiertas {payload.totales?.abiertas ?? 0} · Inactivas {payload.totales?.inactivas ?? 0}
            </span>
            <Button size="sm" variant="outline" data-testid="desempeno-export-csv"
                    onClick={() => crmTerritorialAPI
                      .descargarDesempenoCsv(seccion || undefined, dias || undefined)
                      .catch((e) => toast({ title: 'Exportación rechazada',
                        description: e.message, variant: 'destructive' }))}>
              <Download className="h-4 w-4 mr-2" />Exportar CSV
            </Button>
            <span className="text-gray-400 text-xs">
              Sin correo ni teléfono en el CSV. Cálculo determinista, sin IA.
            </span>
          </div>
        )}

        {vista === 'metas' && payload && (
          <div className="flex flex-wrap items-center gap-3 text-sm" data-testid="metas-barra">
            {['verde', 'ambar', 'rojo', 'sin_meta'].map((c) => (
              <Badge key={c} className={SEMAFORO[c]} data-testid={`metas-resumen-${c}`}>
                {c}: {payload.resumen_semaforo?.[c] ?? 0}
              </Badge>
            ))}
            <Button size="sm" onClick={() => setNuevaMeta(true)} data-testid="metas-nueva-btn">
              Nueva meta
            </Button>
            <span className="text-gray-400 text-xs">
              Verde ≥100% · Ámbar ≥70% · Rojo por debajo. Sin IA y sin alertas externas.
            </span>
          </div>
        )}

        {vista === 'clientes' && payload && (
          <div className="flex flex-wrap items-center gap-3 text-sm"
               data-testid="clientes-barra">
            <Button size="sm" variant="outline" onClick={() => setAgenteAtencion(true)}
                    data-testid="clientes-agente-atencion-btn">
              Agente de atención
            </Button>
            <span className="text-gray-400 text-xs">
              Perfila al cliente de forma determinista, lo registra en el CRM y muestra sólo
              inventario autorizado de la sección con el motivo de cada coincidencia. Sin IA.
            </span>
          </div>
        )}

        {vista === 'prospectos' && payload && (
          <div className="flex flex-wrap items-center gap-3 text-sm"
               data-testid="prospectos-barra">
            <Button size="sm" onClick={() => setNuevoProspecto(true)}
                    data-testid="prospectos-nuevo-btn">
              Nuevo prospecto
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAgenteAfiliacion(true)}
                    data-testid="prospectos-agente-afiliacion-btn">
              Agente de afiliación
            </Button>
            <Button size="sm" variant="outline" disabled={seleccion.length === 0}
                    onClick={() => setReasignando(true)}
                    data-testid="prospectos-reasignar-masivo-btn">
              Reasignar seleccionados ({seleccion.length})
            </Button>
            <span className="text-gray-400 text-xs">
              Alta manual con deduplicación por correo y teléfono, asignación territorial y
              auditoría. Reasignación masiva atómica con previsualización. Sin envíos externos.
            </span>
          </div>
        )}

        {vista === 'propiedades' && payload && (
          <div className="flex flex-wrap items-center gap-3 text-sm"
               data-testid="propiedades-barra">
            <Button size="sm" onClick={() => setPropForm({})}
                    data-testid="propiedades-nueva-btn">
              Nueva propiedad
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAgente(true)}
                    data-testid="propiedades-agente-btn">
              Agente de inventario
            </Button>
            <span className="text-gray-400 text-xs">
              Captura y edición territorial con datos estructurados únicamente (sin imágenes),
              auditoría antes/después y aislamiento por sección.
            </span>
          </div>
        )}

        {vista === 'auditoria' && payload && (
          <div className="flex flex-wrap items-end gap-2 text-sm" data-testid="bitacora-barra">
            <input value={filtros.actor} placeholder="Actor (nombre)"
                   onChange={(e) => setFiltros({ ...filtros, actor: e.target.value })}
                   className="border rounded-lg px-2 py-1 w-44" data-testid="bitacora-actor" />
            <select value={filtros.accion}
                    onChange={(e) => setFiltros({ ...filtros, accion: e.target.value })}
                    className="border rounded-lg px-2 py-1 bg-white"
                    data-testid="bitacora-accion">
              <option value="">Toda acción</option>
              {(payload.acciones_disponibles || []).map((a) => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
            <select value={filtros.resultado}
                    onChange={(e) => setFiltros({ ...filtros, resultado: e.target.value })}
                    className="border rounded-lg px-2 py-1 bg-white"
                    data-testid="bitacora-resultado-filtro">
              <option value="">Todo resultado</option>
              <option value="success">success</option>
              <option value="denied">denied</option>
            </select>
            <input type="date" value={filtros.desde}
                   onChange={(e) => setFiltros({ ...filtros, desde: e.target.value })}
                   className="border rounded-lg px-2 py-1" data-testid="bitacora-desde" />
            <input type="date" value={filtros.hasta}
                   onChange={(e) => setFiltros({ ...filtros, hasta: e.target.value })}
                   className="border rounded-lg px-2 py-1" data-testid="bitacora-hasta" />
            <Button size="sm" onClick={() => cargar(seccion, vista, dias)}
                    data-testid="bitacora-aplicar">
              Aplicar
            </Button>
            <Button size="sm" variant="outline" data-testid="bitacora-export-csv"
                    onClick={() => crmTerritorialAPI
                      .descargarBitacoraCsv(seccion || undefined, filtros)
                      .catch((e) => toast({ title: 'Exportación rechazada',
                        description: e.message, variant: 'destructive' }))}>
              <Download className="h-4 w-4 mr-2" />CSV
            </Button>
            <span className="text-gray-400 text-xs">
              Sólo nombre del actor · correos e identificadores ocultos · sólo CRM territorial
            </span>
          </div>
        )}

        {vista === 'supervision' && (
          <PanelSupervisionAgentes seccion={seccion} />
        )}

        {vista === 'audiencias' && (
          <PanelAudienciasMarketing seccion={seccion} />
        )}

        {vista !== 'supervision' && vista !== 'audiencias' && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {VISTAS.find((v) => v.id === vista)?.label}
              {payload && ` · ${payload.total ?? payload.asesores} registro(s)`}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {cargando
              ? <div className="flex items-center text-gray-500 py-6">
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" /> Cargando…
                </div>
              : <Tabla columnas={tabla.columnas} filas={tabla.filas}
                       testid={`crm-territorial-tabla-${vista}`} />}
          </CardContent>
        </Card>
        )}

        {seleccionado && (
          <ProspectoAcciones prospecto={seleccionado} asesores={listaAsesores}
                             onCerrar={() => setSeleccionado(null)}
                             onActualizado={() => cargar(seccion, vista, dias)} />
        )}

        {cliente && (
          <ClienteCoincidencias cliente={cliente} onCerrar={() => setCliente(null)}
                                onActualizado={() => cargar(seccion, vista, dias)} />
        )}

        {oportunidad && (
          <OportunidadEtapa oportunidad={oportunidad} asesores={listaAsesores}
                            onCerrar={() => setOportunidad(null)}
                            onActualizado={() => cargar(seccion, vista, dias)} />
        )}

        {nuevoProspecto && (
          <ProspectoNuevo asesores={listaAsesores} seccionActual={seccion}
                          onCerrar={() => setNuevoProspecto(false)}
                          onGuardado={() => cargar(seccion, vista, dias)} />
        )}

        {nuevaMeta && (
          <MetaTerritorialForm secciones={secciones} asesores={listaAsesores}
                               seccionActual={seccion} onCerrar={() => setNuevaMeta(false)}
                               onGuardado={() => cargar(seccion, vista, dias)} />
        )}

        {reasignando && (
          <ReasignacionMasiva
            prospectos={items.filter((x) => seleccion.includes(x.oportunidad_id))}
            asesores={listaAsesores} onCerrar={() => setReasignando(false)}
            onHecho={() => { setSeleccion([]); cargar(seccion, vista, dias); }} />
        )}

        {propForm && (
          <PropiedadForm propiedad={propForm.propiedad_id ? propForm : null}
                         asesores={listaAsesores} seccionActual={seccion}
                         onCerrar={() => setPropForm(null)}
                         onGuardado={() => cargar(seccion, vista, dias)} />
        )}
        {agente && (
          <AgenteInventario asesores={listaAsesores} seccionActual={seccion}
                            onCerrar={() => setAgente(false)}
                            onGuardado={() => cargar(seccion, vista, dias)} />
        )}
        {agenteAtencion && (
          <AgenteAtencion asesores={listaAsesores} seccionActual={seccion}
                          onCerrar={() => setAgenteAtencion(false)}
                          onGuardado={() => cargar(seccion, vista, dias)} />
        )}
        {agenteAfiliacion && (
          <AgenteAfiliacion asesores={listaAsesores} seccionActual={seccion}
                            onCerrar={() => setAgenteAfiliacion(false)}
                            onGuardado={() => cargar(seccion, vista, dias)} />
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminCRMTerritorial;
